import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import baseURL from '../utils/baseURL';
import useAxiosAuth from '../hooks/useAxiosAuth';

// ==================== DOCTOR MEDICINE MASTER ====================

export const createDoctorMedicine = createAsyncThunk(
    'medicine/createDoctorMedicine',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`${baseURL}/doctor-medicine`, payload);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getDoctorMedicines = createAsyncThunk(
    'medicine/getDoctorMedicines',
    async ({ doctorId, page = 1, limit = 50, search = '', type = 'all' } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(
                `${baseURL}/doctor-medicine/doctor/${doctorId}`,
                { params: { page, limit, search, type } }
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateDoctorMedicine = createAsyncThunk(
    'medicine/updateDoctorMedicine',
    async ({ medicineId, payload }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(
                `${baseURL}/doctor-medicine/${medicineId}`,
                payload
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deleteDoctorMedicine = createAsyncThunk(
    'medicine/deleteDoctorMedicine',
    async (medicineId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(
                `${baseURL}/doctor-medicine/${medicineId}`
            );
            return { ...response.data, medicineId };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// ==================== APPOINTMENT PRESCRIPTION ====================

export const createPrescription = createAsyncThunk(
    'medicine/createPrescription',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(
                `${baseURL}/appointment-prescription`,
                payload
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const getPrescriptionsByAppointment = createAsyncThunk(
    'medicine/getPrescriptionsByAppointment',
    async (appointmentId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(
                `${baseURL}/appointment-prescription/appointment/${appointmentId}`
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updatePrescription = createAsyncThunk(
    'medicine/updatePrescription',
    async ({ prescriptionId, payload }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(
                `${baseURL}/appointment-prescription/${prescriptionId}`,
                payload
            );
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deletePrescription = createAsyncThunk(
    'medicine/deletePrescription',
    async (prescriptionId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.delete(
                `${baseURL}/appointment-prescription/${prescriptionId}`
            );
            return { ...response.data, prescriptionId };
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const medicineSlice = createSlice({
    name: 'medicine',
    initialState: {
        medicines: [],
        prescriptions: [],
        loading: false,
        submitting: false,
        deletingId: null,
        prescriptionsLoading: false,
        error: null,
    },
    reducers: {
        clearMedicineError: (state) => {
            state.error = null;
        },
        clearPrescriptions: (state) => {
            state.prescriptions = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // ---- master list ----
            .addCase(getDoctorMedicines.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getDoctorMedicines.fulfilled, (state, action) => {
                state.loading = false;
                state.medicines = action.payload?.data || [];
            })
            .addCase(getDoctorMedicines.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(createDoctorMedicine.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(createDoctorMedicine.fulfilled, (state, action) => {
                state.submitting = false;
                if (action.payload?.data) {
                    state.medicines.unshift(action.payload.data);
                }
            })
            .addCase(createDoctorMedicine.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload;
            })
            .addCase(updateDoctorMedicine.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(updateDoctorMedicine.fulfilled, (state, action) => {
                state.submitting = false;
                const updated = action.payload?.data;
                if (updated) {
                    const idx = state.medicines.findIndex(
                        (m) => m.medicineId === updated.medicineId
                    );
                    if (idx !== -1) state.medicines[idx] = updated;
                }
            })
            .addCase(updateDoctorMedicine.rejected, (state, action) => {
                state.submitting = false;
                state.error = action.payload;
            })
            .addCase(deleteDoctorMedicine.pending, (state, action) => {
                state.deletingId = action.meta.arg;
                state.error = null;
            })
            .addCase(deleteDoctorMedicine.fulfilled, (state, action) => {
                state.deletingId = null;
                const id = action.payload?.medicineId || action.payload?.data?.medicineId;
                if (id) {
                    state.medicines = state.medicines.filter((m) => m.medicineId !== id);
                }
            })
            .addCase(deleteDoctorMedicine.rejected, (state, action) => {
                state.deletingId = null;
                state.error = action.payload;
            })

            // ---- prescriptions ----
            .addCase(getPrescriptionsByAppointment.pending, (state) => {
                state.prescriptionsLoading = true;
                state.error = null;
            })
            .addCase(getPrescriptionsByAppointment.fulfilled, (state, action) => {
                state.prescriptionsLoading = false;
                state.prescriptions = action.payload?.data || [];
            })
            .addCase(getPrescriptionsByAppointment.rejected, (state, action) => {
                state.prescriptionsLoading = false;
                state.error = action.payload;
            })
            .addCase(createPrescription.fulfilled, (state, action) => {
                if (action.payload?.data) {
                    state.prescriptions.push(action.payload.data);
                }
            })
            .addCase(updatePrescription.fulfilled, (state, action) => {
                const updated = action.payload?.data;
                if (updated) {
                    const idx = state.prescriptions.findIndex(
                        (p) => p.prescriptionId === updated.prescriptionId
                    );
                    if (idx !== -1) state.prescriptions[idx] = updated;
                }
            })
            .addCase(deletePrescription.fulfilled, (state, action) => {
                const id = action.payload?.prescriptionId || action.payload?.data?.prescriptionId;
                if (id) {
                    state.prescriptions = state.prescriptions.filter(
                        (p) => p.prescriptionId !== id
                    );
                }
            });
    },
});

export const { clearMedicineError, clearPrescriptions } = medicineSlice.actions;
export default medicineSlice.reducer;
